import _ from "lodash";

import {
  FIELD_TYPE_IDS,
  NUMBER_COMPARISON_OPERATORS,
  NUMBER_COMPARISON_SEPARATOR,
} from "../constants";
import {
  ICustomField,
  IFilterSchemaObject,
  ITypeormWhereCriteria,
  OperatorDescriptor,
  RydeAdminUnit,
  RydeUiUnit,
  TDropdownNullableOption,
  TypeOrmMappedOperator,
} from "../models";
import { normalizeToArrayValue } from "./array.helpers";
import { controlNameMetadataResolve } from "./dynamic-forms.helpers";
import { hasKeys } from "./objects.helpers";

export const getConstInitialFiltersByEntity = (
  entity: RydeUiUnit,
): IFilterSchemaObject => {
  switch (entity) {
    default:
      return {};
  }
};

export const transformFiltersIdsToStoreObj = (
  ids: number[] | undefined,
): IFilterSchemaObject | undefined => {
  if (!ids?.length) {
    return undefined;
  }

  const obj = ids.reduce(
    (accumulator, value) => ({ ...accumulator, [value]: null }),
    {},
  );
  return obj;
};

export const clearValuesFilterStoreObj = (
  filterObj: IFilterSchemaObject,
): IFilterSchemaObject => {
  if (!hasKeys(filterObj)) {
    return {};
  }

  const obj = Object.keys(filterObj).reduce(
    (accumulator, value) => ({ ...accumulator, [value]: null }),
    {},
  );
  return obj;
};

export const transformFilterStoreObjectToCriteria = <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T extends Record<string, any>,
>(
  filterObj: IFilterSchemaObject,
  controls: ICustomField[],
): ITypeormWhereCriteria<T> => {
  if (!hasKeys(filterObj)) {
    return {};
  }

  const filterCriteria: ITypeormWhereCriteria<T> = {};

  for (const customFieldId in filterObj) {
    const val = filterObj[customFieldId];

    if (!val || _.isEmpty(val)) {
      continue;
    }

    const control = controls.find(x => x.id === +customFieldId);

    if (!control?.name) {
      continue;
    }

    const normalizedArrVal = normalizeToArrayValue(val);

    if (!validateFilterCriteriaForField(control, normalizedArrVal)) {
      continue;
    }

    const fieldName = controlNameMetadataResolve(control);
    const whereFieldName = control.gridAttributes?.whereFieldName;
    const whereSubFieldName = control.gridAttributes?.whereSubFieldName;

    if (!whereSubFieldName) {
      filterCriteria[fieldName as keyof T] = buildFilterCriteriaForField(
        control,
        normalizedArrVal,
      );
    } else {
      const nestedFieldName = whereFieldName || fieldName;

      if (!filterCriteria[nestedFieldName]) {
        filterCriteria[nestedFieldName as keyof T] = {};
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (filterCriteria[nestedFieldName] as ITypeormWhereCriteria<any>)[
        whereSubFieldName
      ] = buildFilterCriteriaForField(control, normalizedArrVal);
    }
  }

  return filterCriteria;
};

export const getConstInitialFiltersAdminByEntity = (
  entity: RydeAdminUnit,
): IFilterSchemaObject => {
  switch (entity) {
    case "customField":
      return {};
    default:
      return {};
  }
};

/**
 * Decode what the comparison filter stored: one string holding both the operator and the number.
 *
 * Returns null for anything it cannot read — an operator with no number yet, a symbol no longer
 * offered — so an incomplete filter is skipped rather than sent as a comparison against nothing.
 */
export const parseNumberComparisonValue = (
  value: TDropdownNullableOption[],
): { criteria: TypeOrmMappedOperator; operand: number } | null => {
  const [encoded] = value;

  if (typeof encoded !== "string") {
    return null;
  }

  const separatorIndex = encoded.indexOf(NUMBER_COMPARISON_SEPARATOR);
  const symbol = encoded.slice(0, separatorIndex);
  const operandText = encoded.slice(separatorIndex + 1);
  const operand = Number(operandText);
  const operator = NUMBER_COMPARISON_OPERATORS.find(
    item => item.value === symbol,
  );

  // The empty-text check has to come before the numeric one: Number("") is 0 and 0 is finite, so
  // testing only the parsed number turns "greater than <nothing typed yet>" into "greater than 0".
  if (
    separatorIndex < 0 ||
    !operator ||
    !operandText ||
    !Number.isFinite(operand)
  ) {
    return null;
  }

  return { criteria: operator.criteria, operand };
};

const isNumberComparison = (control: ICustomField): boolean =>
  control.filterFieldTypeId === FIELD_TYPE_IDS.FILTER.number_comparison;

export const validateFilterCriteriaForField = (
  control: ICustomField,
  value: TDropdownNullableOption[],
): boolean => {
  if (control.filterAttributes?.operatorBetween) {
    return value.length === 2;
  }

  if (isNumberComparison(control)) {
    return parseNumberComparisonValue(value) !== null;
  }

  return true;
};

export const buildFilterCriteriaForField = (
  control: ICustomField,
  value: TDropdownNullableOption[],
): OperatorDescriptor => {
  // The only filter whose operator the user chooses, so it is the only one that cannot take the
  // default. Validation above has already guaranteed this parses.
  if (isNumberComparison(control)) {
    const parsed = parseNumberComparisonValue(value);

    return parsed ? { [parsed.criteria]: parsed.operand } : {};
  }

  let operator = "$in";

  if (control.filterAttributes?.operatorBetween) {
    operator = "$between";
  } else if (
    control.gridAttributes?.whereOperator ||
    control.jsonType === "array"
  ) {
    operator = "$overlaps";
  }

  const filterCriteria: OperatorDescriptor = {
    [operator]: value,
  };
  return filterCriteria;
};

export const isEmptyFilterValue = (value: unknown): boolean =>
  value === undefined ||
  value === null ||
  value === "" ||
  (Array.isArray(value) && value.length === 0);

/**
 * The inverse, as a type predicate — so a caller that guards on it can go on to
 * use the value without re-proving it isn't null.
 */
export const hasFilterValue = <T>(value: T): value is NonNullable<T> =>
  !isEmptyFilterValue(value);
